import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setFilterQuery } from '../../redux/Slices/filterQuerySlice';
import './scss/Filter.scss';
import Checkbox from '../Global/Checkbox';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import { selectBoardId } from '../../redux/Slices/boardIdSlice';
import { areFiltersEqual, find, getFilters, initialFiltersState, rgbaColor, updateFiltersOnly } from '../../utils/helpers';
import useOutsideClick from '../../hooks/useOutsideClick';
import { config } from '../../config';
import { fetchBoardById } from '../../redux/Slices/boardSlice';
import ClearAll from '../icons/ClearAll';
import Close from '../icons/Close';
import Person from '../icons/Person';
import KeyboardArrowDown from '../icons/KeyboardArrowDown';
import Event from '../icons/Event';
import EventBusy from '../icons/EventBusy';
import EventUpcoming from '../icons/EventUpcoming';
import EventNote from '../icons/EventNote';
import EventAvailable from '../icons/EventAvailable';
import Label from '../icons/Label';

const Filter = ({ setBoard, setLoading, setShowFilter, cardCount, setCardCount }) => {
    const { user } = useSelector((state) => state.user);
    const filterQuery = useSelector((state) => state.filterQuery);
    const { workspaces } = useSelector((state) => state.workspaces);
    const boardId = useSelector(selectBoardId);
    const members = useSelector(state => state.member.boardMembers || state.member.workspaceMembers || []);

    const [filters, setFilters] = useState(filterQuery);
    const [searchTerm, setSearchTerm] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [isShowFilteredMembers, setShowFilteredMembers] = useState(false);
    const [searchSelectedMembers, setSearchSelectedMembers] = useState([]);
    const [isUpdatingFilters, setIsUpdatingFilters] = useState(false);
    const [showAllLabels, setShowAllLabels] = useState(false);
    const [labelSearchTerm, setLabelSearchTerm] = useState('');
    const [isLabelFocused, setIsLabelFocused] = useState(false);
    const [isShowFilteredLabels, setShowFilteredLabels] = useState(false);

    const assignRef = useRef();
    const selectMembersRef = useRef();
    const selectLabelsRef = useRef();
    const debounceTimeoutRef = useRef();
    const dispatch = useDispatch();

    // Helper function to check if filters are in initial state
    const isFiltersInitialState = (filters) => {
        const cleanFilters = {
            keyword: filters.keyword || '',
            noMembers: filters.noMembers || false,
            selectMembers: filters.selectMembers || false,
            assignedToMe: filters.assignedToMe || false,
            noDates: filters.noDates || false,
            overdue: filters.overdue || false,
            dueNextDay: filters.dueNextDay || false,
            dueInTheNextWeek: filters.dueInTheNextWeek || false,
            dueInTheNextMonth: filters.dueInTheNextMonth || false,
            noLabels: filters.noLabels || false,
            selectedMembers: filters.selectedMembers || [],
            selectedLabels: filters.selectedLabels || [],
            labels: filters.labels || []
        };

        return (
            cleanFilters.keyword === '' &&
            !cleanFilters.noMembers &&
            !cleanFilters.selectMembers &&
            !cleanFilters.assignedToMe &&
            !cleanFilters.noDates &&
            !cleanFilters.overdue &&
            !cleanFilters.dueNextDay &&
            !cleanFilters.dueInTheNextWeek &&
            !cleanFilters.dueInTheNextMonth &&
            !cleanFilters.noLabels &&
            cleanFilters.selectedMembers.length === 0 &&
            cleanFilters.selectedLabels.length === 0
        );
    };

    // Optimized filter update function with configurable debouncing
    const updateFilters = useCallback((newFilters, delay = 300) => {
        setIsUpdatingFilters(true);

        // Clear existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Set new timeout with configurable delay
        debounceTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await updateFiltersOnly({
                    filters: newFilters,
                    boardId
                });

                if (response && response.cardCount !== undefined) {
                    setCardCount(response.cardCount);
                }

                // Update Redux state only after successful API call
                dispatch(setFilterQuery(newFilters));
            } catch (error) {
                console.error('Error updating filters:', error);
            } finally {
                setIsUpdatingFilters(false);
            }
        }, delay);
    }, [boardId, setCardCount, dispatch]);

    // Sync local filters state with Redux only on component mount or when filters are cleared externally
    useEffect(() => {
        // Only sync from Redux to local state if local state is empty/initial or if it's a clear operation
        const isLocalInitial = isFiltersInitialState(filters);
        const isReduxInitial = isFiltersInitialState(filterQuery);

        // Sync only if local state is initial and Redux has data, or if Redux was cleared
        if ((isLocalInitial && !isReduxInitial) || (isReduxInitial && !isLocalInitial)) {
            setFilters(filterQuery);
        }
    }, [filterQuery.filteredStatus]); // Only watch filteredStatus to avoid keyword conflicts

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);


    const handleChange = (e) => {
        const { name, checked } = e.target;
        let newFilters = { ...filters, [name]: checked };

        // Check if filters are in initial state
        const isInitialState = isFiltersInitialState(newFilters);
        newFilters.filteredStatus = !isInitialState;

        setFilters(newFilters);
        updateFilters(newFilters, 150); // Shorter debounce for checkboxes (150ms)
    };

    const handleKeywordChange = (e) => {
        let newFilters = { ...filters, keyword: e.target.value };

        // Check if filters are in initial state
        const isInitialState = isFiltersInitialState(newFilters);
        newFilters.filteredStatus = !isInitialState;

        setFilters(newFilters);
        updateFilters(newFilters, 500); // Longer debounce for keyword input (500ms)

    };

    const handleSelectLabel = (label) => {
        // Store label text instead of entire label object to match backend expectations
        const labelText = label.text;
        const newSelectedLabels = filterQuery.selectedLabels.includes(labelText)
            ? filterQuery.selectedLabels.filter(selected => selected !== labelText)
            : [...filterQuery.selectedLabels, labelText];

        let newFilters = { ...filters, selectedLabels: newSelectedLabels };

        // Check if filters are in initial state
        const isInitialState = isFiltersInitialState(newFilters);
        newFilters.filteredStatus = !isInitialState;

        setFilters(newFilters);
        updateFilters(newFilters, 200); // Medium debounce for label selection (200ms)

        setIsLabelFocused(true);

    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setFilters(prev => ({ ...prev, selectMembers: true }));
    };

    useOutsideClick(selectMembersRef, () => setShowFilteredMembers(false));
    useOutsideClick(selectLabelsRef, () => {
        setShowFilteredLabels(false);
        setIsLabelFocused(false);
    });

    const handleSelectMembersCheckbox = (checked) => {
        let newSelectedMembers;

        if (checked) {
            // Select all members except the current user
            newSelectedMembers = members.map(member => member._id).filter(memberId => memberId !== user?.user?._id);
        } else {
            // Unselect all members
            newSelectedMembers = [];
        }

        let newFilters = { ...filters, selectMembers: checked, selectedMembers: newSelectedMembers };

        // Check if filters are in initial state
        const isInitialState = isFiltersInitialState(newFilters);
        newFilters.filteredStatus = !isInitialState;

        setFilters(newFilters);
        updateFilters(newFilters, 200); // Medium debounce for member selection (200ms)

        // Update the local search selected members state if necessary
        setSearchSelectedMembers(newSelectedMembers);
    };



    const handleSelectUser = (member) => {
        const isAlreadySelected = filters.selectedMembers.includes(member._id);

        let newSelectedMembers;

        if (isAlreadySelected) {
            newSelectedMembers = filters.selectedMembers.filter(selected => selected !== member._id);
        } else {
            newSelectedMembers = [...filters.selectedMembers, member._id];
        }

        const isAnyMemberSelected = newSelectedMembers.length > 0;

        let newFilters = {
            ...filters,
            selectedMembers: newSelectedMembers,
            selectMembers: isAnyMemberSelected
        };

        // Check if filters are in initial state
        const isInitialState = isFiltersInitialState(newFilters);
        newFilters.filteredStatus = !isInitialState;

        setFilters(newFilters);
        updateFilters(newFilters, 200); // Medium debounce for user selection (200ms)
    };

    const handleAssignedToMeChange = (checked) => {
        const isAlreadySelected = filters.selectedMembers.includes(user?.user?._id);

        let newSelectedMembers;

        if (checked && !isAlreadySelected) {
            newSelectedMembers = [...filters.selectedMembers, user?.user?._id];
        } else if (!checked && isAlreadySelected) {
            newSelectedMembers = filters.selectedMembers.filter(memberId => memberId !== user?.user?._id);
        } else {
            newSelectedMembers = filters.selectedMembers;
        }

        let newFilters = {
            ...filters,
            assignedToMe: checked,
            selectedMembers: newSelectedMembers,
        };

        // Check if filters are in initial state
        const isInitialState = isFiltersInitialState(newFilters);
        newFilters.filteredStatus = !isInitialState;

        setFilters(newFilters);
        updateFilters(newFilters, 150); // Short debounce for assigned to me toggle (150ms)
    };

    const getPlaceholderText = () => {
        const visibleSelectedMembers = filterQuery.selectedMembers.filter(
            memberId => memberId !== user?.user?._id
        );

        if (visibleSelectedMembers.length === 0) {
            return 'Select an assign';
        }

        return `${visibleSelectedMembers.length} member${visibleSelectedMembers.length > 1 ? 's' : ''} selected`;
    };

    const getLabelPlaceholderText = () => {
        if (filterQuery.selectedLabels.length === 0) {
            return 'Select labels';
        }
        return `${filterQuery.selectedLabels.length} label${filterQuery.selectedLabels.length > 1 ? 's' : ''} selected`;
    };

    const handleLabelSearchChange = (e) => {
        setLabelSearchTerm(e.target.value);
    };

    const filteredLabels = filterQuery.labels ? filterQuery.labels.filter(label =>
        label.text.toLowerCase().includes(labelSearchTerm.toLowerCase())
    ) : [];

    // The rest of your component code

    const filteredMembers = members.filter(member =>
        member.name && // Ensure member has an _id
        member._id !== user?.user?._id && // Exclude the current user
        (member.name.toLowerCase().includes(searchTerm.toLowerCase()) || // Match by name
            member.username.toLowerCase().includes(searchTerm.toLowerCase())) // Match by username
    );

    const handleClearFilters = () => {
        const clearedFilters = { ...initialFiltersState, filteredStatus: false };
        setFilters(clearedFilters);
        updateFilters(clearedFilters, 0); // No debounce for clear action (immediate)
    }


    console.log({ filterQuery });

    return (
        <div className="filter-container">
            <h3 className="filter-title-wrapper">
                <span
                    className='clear-all'
                    data-tooltip-content="Clear all"
                    data-tooltip-position="top"
                    onClick={handleClearFilters}
                >
                    <ClearAll size={18} />
                </span>
                <span className='filter-title'>Filter</span>
                <span className='filter-close' onClick={() => setShowFilter(false)}>
                    <Close size={18} />
                </span>
            </h3>
            <input
                label="Enter a keyword..."
                variant="outlined"
                size="small"
                value={filters.keyword}
                onChange={handleKeywordChange}
                className='keyword'
                placeholder='Enter a keyword'
            />
            <span className='search-description'>Search cards, members, labels, and more.</span>

            {/* Members Filter */}
            <div className="filter-section">
                <h4>Members</h4>
                <div className="filter-options">
                    <Checkbox
                        checked={filters.noMembers}
                        onChange={(checked) => handleChange({ target: { name: 'noMembers', checked } })}
                        id="no-members-checkbox"
                    />
                    <Person size={20} color='var(--filter-icon-person-background)' />
                    <span>No members</span>
                </div>
                <div className="filter-options">
                    <Checkbox
                        checked={filters.assignedToMe}
                        onChange={handleAssignedToMeChange}
                        id="assigned-to-me-checkbox"
                    />
                    {user && (
                        user?.user?.profilePicture ? (
                            <img
                                src={user?.user?.profilePicture}
                                alt={user?.user?.username}
                                style={{ width: "22px", height: "22px", borderRadius: "50%" }}
                                loading="lazy"
                            />
                        ) : (
                            <ImagePlaceholder key={user?.user?._id} size={22} text={user?.user?.username} />
                        )
                    )}
                    <span>Cards assigned to me</span>
                </div>
                <div className="filter-options" ref={selectMembersRef}>
                    <Checkbox
                        checked={filters.selectMembers}
                        onChange={handleSelectMembersCheckbox}
                        id="select-all-members-checkbox"
                    />


                    <div className='select-members'>
                        <div className={`select-option-label${isFocused ? ' focus' : ''}`} ref={assignRef}>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                placeholder={getPlaceholderText()}
                                onFocus={() => {
                                    setIsFocused(true);
                                    setShowFilteredMembers(true);
                                }}
                                onBlur={() => setIsFocused(false)}
                            />
                            <span className="material-symbols-outlined">
                                <KeyboardArrowDown size={20} />
                            </span>
                        </div>

                        {isShowFilteredMembers && (
                            <div className='board-members'>
                                {filteredMembers.length > 0 && (
                                    <div className="sbm-search-results">
                                        {filteredMembers.map((member, index) => {

                                            return (
                                                <div className='label'
                                                    key={index}
                                                    onClick={() => handleSelectUser(member)} // Unified click handler for the entire label
                                                    style={{ cursor: 'pointer' }} // Change cursor to pointer to indicate clickable area
                                                >
                                                    <Checkbox
                                                        checked={filters.selectedMembers.includes(member._id)}
                                                        onChange={() => handleSelectUser(member)} // Keep this for accessibility, but it's redundant now
                                                        id={`select-members-checkbox-${member._id}`}
                                                    />

                                                    <div className="mention-member">
                                                        {member.profilePicture ? (
                                                            <img src={member.profilePicture} style={{ width: '16px', height: '16px' }} alt={member.name} loading="lazy" />
                                                        ) : (
                                                            <ImagePlaceholder key={member._id} size={16} text={member.username} />
                                                        )}
                                                        <div className="suggession-handler">{`${member.name}`}</div>
                                                    </div>
                                                </div>

                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Due Date Filter */}
            <div className="filter-section">
                <h4>Due date</h4>
                <div className="filter-options">
                    <Checkbox
                        checked={filters.noDates}
                        onChange={(checked) => handleChange({ target: { name: 'noDates', checked } })}
                        id="no-dates-checkbox"
                    />
                    <Event size={20} color='var(--filter-icon-background)' />
                    <span>No due date</span>
                </div>
                <div className="filter-options">
                    <Checkbox
                        checked={filters.overdue}
                        onChange={(checked) => handleChange({ target: { name: 'overdue', checked } })}
                        id="overdue-checkbox"
                    />
                    <EventBusy size={20} color='var(--filter-icon-overdue-background)' />
                    <span>Overdue</span>
                </div>
                <div className="filter-options">
                    <Checkbox
                        checked={filters.dueNextDay}
                        onChange={(checked) => handleChange({ target: { name: 'dueNextDay', checked } })}
                        id="due-in-next-day-checkbox"
                    />
                    <EventUpcoming size={20} color='var(--filter-icon-background)' />
                    <span>Due in the next day</span>
                </div>
                <div className="filter-options">
                    <Checkbox
                        checked={filters.dueInTheNextWeek}
                        onChange={(checked) => handleChange({ target: { name: 'dueInTheNextWeek', checked } })}
                        id="due-in-next-week-checkbox"
                    />
                    <EventNote size={20} color='var(--filter-icon-background)' />
                    <span>Due in the next week</span>
                </div>
                <div className="filter-options">
                    <Checkbox
                        checked={filters.dueInTheNextMonth}
                        onChange={(checked) => handleChange({ target: { name: 'dueInTheNextMonth', checked } })}
                        id="due-in-next-month-checkbox"
                    />
                    <EventAvailable size={20} color='var(--filter-icon-background)' />
                    <span>Due in the next month</span>
                </div>
            </div>

            {/* Labels Filter */}
            <div className="filter-section">
                <h4>Labels</h4>
                <div className="filter-options">
                    <Checkbox
                        checked={filters.noLabels}
                        onChange={(checked) => handleChange({ target: { name: 'noLabels', checked } })}
                        id="no-labels-checkbox"
                    />
                    <Label size={20} color='var(--filter-icon-background)' />
                    <span>No labels</span>
                </div>
                <div className="filter-options" ref={selectLabelsRef}>
                    <Checkbox
                        checked={filterQuery.selectedLabels.length > 0}
                        onChange={(checked) => {
                            if (!checked) {
                                // Clear all selected labels
                                let newFilters = { ...filters, selectedLabels: [] };
                                const isInitialState = isFiltersInitialState(newFilters);
                                newFilters.filteredStatus = !isInitialState;
                                setFilters(newFilters);
                                updateFilters(newFilters, 200);
                            }
                        }}
                        id="select-all-labels-checkbox"
                    />

                    <div className='select-labels'>
                        <div className={`select-option-label${isLabelFocused ? ' focus' : ''}`}>
                            <input
                                type="text"
                                value={labelSearchTerm}
                                onChange={handleLabelSearchChange}
                                placeholder={getLabelPlaceholderText()}
                                onFocus={() => {
                                    setIsLabelFocused(true);
                                    setShowFilteredLabels(true);
                                }}
                            // onBlur={() => setIsLabelFocused(false)}
                            />
                            <span className="material-symbols-outlined">
                                <KeyboardArrowDown size={20} />
                            </span>
                        </div>

                        {isShowFilteredLabels && (
                            <div className='board-labels'>
                                {filteredLabels.length > 0 && (
                                    <div className="sbl-search-results">
                                        {filteredLabels.map((label, index) => (
                                            <div className='label-item'
                                                key={index}
                                                onClick={() => handleSelectLabel(label)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Checkbox
                                                    checked={filterQuery.selectedLabels.includes(label.text)}
                                                    onChange={() => handleSelectLabel(label)}
                                                    id={`select-labels-checkbox-${index}`}
                                                />
                                                <div className="label-display">
                                                    <div className="label-box" style={{ color: label.color, backgroundColor: rgbaColor(label.color, 0.1), opacity: 1, fontWeight: 500 }}>
                                                        {label.text}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Show first 3 labels when dropdown is closed */}
                {/* {!isShowFilteredLabels && filterQuery.labels && filterQuery.labels.length > 0 && (
                    <div className="filter-options filter-labels-preview">
                        {filterQuery.labels.slice(0, 3).map((label, index) => (
                            <label key={index} className="label-preview-item">
                                <Checkbox
                                    checked={filterQuery.selectedLabels.includes(label)}
                                    onChange={() => handleSelectLabel(label)}
                                    id={`preview-labels-checkbox-${index}`}
                                />
                                <div className="label-box" style={{ backgroundColor: label.color }}>
                                    {label.text}
                                </div>
                            </label>
                        ))}
                        {filterQuery.labels.length > 3 && (
                            <button
                                className="show-more-labels"
                                onClick={() => {
                                    setShowFilteredLabels(true);
                                    setIsLabelFocused(true);
                                }}
                            >
                                +{filterQuery.labels.length - 3} more
                            </button>
                        )}
                    </div>
                )} */}
            </div>
        </div>
    );
};

export default Filter;
